import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { AdminMenuItem, adminMenuItems } from './adminMenu';
import { clearSession } from '../../services/session';
import {
  getMyNotifications,
  markAllMyNotificationsRead,
  markMyNotificationRead,
  NotificationRecord,
  subscribeNotificationStream,
} from '../../services/expenseService';

type AdminShellProps = {
  pageTitle: string;
  subtitle?: string;
  children: React.ReactNode;
  topRight?: React.ReactNode;
  menuItems?: AdminMenuItem[];
  profileName?: string;
  profileRoleTag?: string;
};

const AdminShell: React.FC<AdminShellProps> = ({
  pageTitle,
  subtitle,
  children,
  topRight,
  menuItems = adminMenuItems,
  profileName = 'Enterprise Admin',
  profileRoleTag = 'ADMIN',
}) => {
  const navigate = useNavigate();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onWindowClick = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    window.addEventListener('click', onWindowClick);
    return () => window.removeEventListener('click', onWindowClick);
  }, []);

  const unreadCount = notifications.filter((item) => !item.is_read).length;

  const formatNotificationTime = (value?: string): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const diffMs = Date.now() - date.getTime();
    const mins = Math.max(1, Math.floor(diffMs / (1000 * 60)));
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const loadNotifications = useCallback(async () => {
    try {
      setNotificationsError(null);
      const rows = await getMyNotifications({ page: 1, perPage: 20 });
      setNotifications(rows);
    } catch (error) {
      if (error instanceof Error) setNotificationsError(error.message);
      else setNotificationsError('Failed to load notifications.');
    }
  }, []);

  useEffect(() => {
    void loadNotifications();
    let refreshTimer: number | null = null;
    const queueRefresh = () => {
      if (refreshTimer) return;
      refreshTimer = window.setTimeout(() => {
        refreshTimer = null;
        void loadNotifications();
      }, 250);
    };
    const unsubscribe = subscribeNotificationStream({
      onEvent: () => queueRefresh(),
      onError: () => queueRefresh(),
    });
    const timer = window.setInterval(() => {
      void loadNotifications();
    }, 45000);
    return () => {
      if (refreshTimer) window.clearTimeout(refreshTimer);
      unsubscribe();
      window.clearInterval(timer);
    };
  }, [loadNotifications]);

  const onReadOne = async (notificationId: string) => {
    try {
      await markMyNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) => (item.id === notificationId ? { ...item, is_read: true } : item)),
      );
    } catch (_) {
      // no-op
    }
  };

  const onReadAll = async () => {
    try {
      await markAllMyNotificationsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch (_) {
      // no-op
    }
  };

  const onSignOut = async () => {
    const token = localStorage.getItem('flintpay_access_token');
    try {
      if (token) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:3001'}/api/auth/signout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (_) {
      // Ignore API errors here; local signout still proceeds.
    } finally {
      clearSession();
      navigate('/login');
    }
  };

  return (
    <div className="grid h-[100vh] overflow-hidden border border-[#243649] bg-[#ebf5f9] lg:grid-cols-[260px_1fr]">
      <aside className="flex h-full flex-col border-b border-[#e3eef5] bg-[#f8fcff] px-5 py-6 lg:border-b-0 lg:border-r">
        <div>
          <p className="text-[24px] font-extrabold leading-none text-[#0e5f78]">FlintPay</p>
          <p className="mt-2 text-[11px] font-bold tracking-[0.14em] text-[#8aa5b4]">Expense Management</p>
        </div>

        <nav className="mt-8 space-y-2.5">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-[14px] px-4 py-3 text-left text-[17px] font-bold tracking-[-0.01em] transition ${isActive
                  ? 'bg-white text-[#0d7d99] shadow-[0_10px_20px_rgba(12,49,76,0.08)]'
                  : 'text-[#6e8594] hover:bg-white/70'
                }`
              }
            >
              <span className="inline-flex w-6 items-center justify-center text-[22px] leading-none opacity-90">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>

      <section className="h-full overflow-y-auto bg-[#ebf5f9]">
        <header className="flex flex-wrap items-center gap-3 border-b border-[#d8e6ee] bg-[#f7fcff] px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="truncate text-[18px] font-extrabold tracking-[-0.02em] text-[#0b5f77]">{pageTitle}</p>
            {subtitle ? <p className="truncate text-[12px] font-semibold text-[#5a8093]">{subtitle}</p> : null}
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div ref={notificationsRef} className="relative">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setIsNotificationsOpen((value) => !value);
                  if (!isNotificationsOpen) loadNotifications();
                }}
                className="relative text-[#6b8394]"
                title="Notifications"
              >
                <span>🔔</span>
                {unreadCount > 0 ? (
                  <span className="absolute -right-2 -top-2 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                ) : null}
              </button>
              {isNotificationsOpen ? (
                <div className="absolute right-0 top-8 z-30 w-[360px] rounded-[12px] border border-[#d6e6ef] bg-white p-2 shadow-[0_12px_28px_rgba(18,55,79,0.18)]">
                  <div className="mb-2 flex items-center justify-between px-2">
                    <p className="text-[13px] font-bold text-[#0f5f78]">Notifications</p>
                    <button
                      type="button"
                      onClick={onReadAll}
                      className="text-[11px] font-bold text-[#0d7d99] hover:text-[#0a6077]"
                    >
                      Mark all read
                    </button>
                  </div>
                  {notificationsError ? (
                    <p className="rounded-md bg-red-50 px-2 py-1 text-[12px] font-semibold text-red-700">{notificationsError}</p>
                  ) : null}
                  <div className="max-h-[340px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="px-2 py-4 text-[12px] font-semibold text-[#6b8394]">No notifications.</p>
                    ) : (
                      <div className="space-y-1">
                        {notifications.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => onReadOne(item.id)}
                            className={`w-full rounded-[10px] border px-2 py-2 text-left transition ${
                              item.is_read
                                ? 'border-[#edf3f7] bg-[#fafcfe]'
                                : 'border-[#d9e9f3] bg-[#eef7fc]'
                            }`}
                          >
                            <p className="text-[12px] font-bold text-[#16465c]">{item.title}</p>
                            {item.message ? <p className="mt-0.5 text-[12px] text-[#4d6b7b]">{item.message}</p> : null}
                            <div className="mt-1 flex items-center justify-between">
                              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#7f98a8]">{item.type.replace('_', ' ')}</span>
                              <span className="text-[10px] font-semibold text-[#7f98a8]">{formatNotificationTime(item.created_at)}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            <span className="text-[#6b8394]">❔</span>
            <div className="h-8 w-px bg-[#d2e1ea]" />
            {topRight || (
              <div ref={profileMenuRef} className="relative flex items-center gap-2">
                <div className="text-right">
                  <p className="text-[13px] font-bold text-[#0f5f78]">{profileName}</p>
                  <p className="text-[10px] font-bold tracking-[0.1em] text-[#7893a5]">{profileRoleTag}</p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsProfileMenuOpen((value) => !value);
                  }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#15344e] text-[14px] text-white"
                >
                  👤
                </button>
                {isProfileMenuOpen ? (
                  <div className="absolute right-0 top-11 z-20 min-w-[140px] rounded-[12px] border border-[#d6e6ef] bg-white p-1 shadow-[0_12px_28px_rgba(18,55,79,0.18)]">
                    <button
                      type="button"
                      onClick={onSignOut}
                      className="w-full rounded-[8px] px-3 py-2 text-left text-[14px] font-bold text-[#2d4c5f] hover:bg-[#edf6fb]"
                    >
                      Sign out
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </header>

        <div className="space-y-5 px-4 py-5 sm:px-6">
          {children}
        </div>
      </section>
    </div>
  );
};

export default AdminShell;
